"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type TriangleCtx,
  TriangleResults,
} from "@/components/tools/linear-algebra/triangle-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type AngleUnit,
  parseAngle,
} from "@/lib/tool-kit/linear-algebra/angle-input";
import {
  type Given,
  solveTriangle,
} from "@/lib/tool-kit/linear-algebra/triangle";

/** English strings. A translated content file overrides these through `ui`. */
export const TRIANGLE_SOLVER_STRINGS = {
  intro:
    "Enter any three of the six parts — at least one of them a side — and leave the rest blank. Side a is opposite angle A, side b opposite B, side c opposite C.",
  unitLabel: "Angle unit",
  units: { degrees: "Degrees", radians: "Radians" },
  exampleLabel: "Load an example",
  examples: {
    none: "Choose an example…",
    sss: "SSS: three sides",
    sas: "SAS: two sides and the angle between",
    asa: "ASA: two angles and the side between",
    aas: "AAS: two angles and a side not between",
    ssaTwo: "SSA: two possible triangles",
    ssaNone: "SSA: no triangle",
  },
  sidesLegend: "Sides",
  anglesLegend: "Angles",
  sideInput: "Side {name}",
  angleInput: "Angle {name}",
  angleHintDegrees: "Between 0 and 180 degrees.",
  angleHintRadians: "Radians, between 0 and π. You can type pi/3 or 2pi/3.",
  results: "Results",
  needThree: "Enter three values (at least one side) to solve the triangle.",
  tooMany:
    "Enter exactly three values. The other three are worked out for you, so clear the extra boxes.",
  needSide:
    "Three angles give the shape of a triangle but not its size. Enter at least one side.",
  invalid:
    "Enter each value as a positive number. Angles must be between 0 and 180 degrees (0 and π radians).",
  angleSum: "These two angles add up to 180° or more, so no triangle has them.",
  inequality:
    "These sides cannot form a triangle: each side must be shorter than the sum of the other two.",
  caseLine: "Case: {case} — {description}",
  caseNames: {
    SSS: "three sides (law of cosines)",
    SAS: "two sides and the included angle (law of cosines, then angles)",
    ASA: "two angles and the included side (angle sum, then law of sines)",
    AAS: "two angles and a non-included side (angle sum, then law of sines)",
    SSA: "two sides and an angle opposite one of them (law of sines; may be ambiguous)",
  },
  noTriangle: "No triangle has these measurements.",
  colourKey:
    "Blue marks the values you entered; orange marks the ones that were solved.",
  triangleN: "Triangle {n}",
  sideLabel: "Side {name}",
  angleLabel: "Angle {name}",
  areaLabel: "Area",
  perimeterLabel: "Perimeter",
  circumradiusLabel: "Circumradius R",
  inradiusLabel: "Inradius r",
  classification: "This is a {bySides}, {byAngles} triangle.",
  bySides: {
    equilateral: "equilateral",
    isosceles: "isosceles",
    scalene: "scalene",
  },
  byAngles: { right: "right", acute: "acute", obtuse: "obtuse" },
  copied: "Copied!",
  copyResults: "Copy results",
  degreesUnit: "{value}°",
  radiansUnit: "{value} rad",
  areaLine: "Area = ½ ab sin C = {value}",
  angleSumLine: "{k} = {total} − {i} − {j} = {result}",
  cosineAngleLine:
    "cos {K} = ({i}² + {j}² − {k}²) / (2{i}{j}) = ({ki}² + {kj}² − {kk}²) / (2 × {ki} × {kj}) = {cos}, so {K} = {result}",
  cosineSideLine:
    "{k}² = {i}² + {j}² − 2{i}{j} cos {K} = {si}² + {sj}² − 2 × {si} × {sj} × cos({angle}) = {sum}, so {k} = {result}",
  sineSideLine:
    "{to} = {from} × sin {To} / sin {From} = {sFrom} × {sinTo} / {sinFrom} = {result}",
  ssaSineLine: "sin {Y} = {y} sin {X} / {x} = {sy} × {sinX} / {sx} = {sinY}",
  ssaFirst: "The acute solution: {Y} = {result}",
  ssaOnly: "{Y} = {result}",
  ssaSecond: "The obtuse solution: {Y}′ = 180° − {Y} = {result}",
  ssaHeading: "The ambiguous case (SSA)",
  ssaHeight: "Height h = {y} sin {X} = {value}, compared with side {x}",
  ambiguityKinds: {
    "none-short":
      "Side {x} is shorter than the height h, so it cannot reach the base line: there is no triangle.",
    "none-obtuse":
      "The known angle is obtuse (or right) and the opposite side is not the longest, so there is no triangle.",
    right:
      "Side {x} equals the height h, so it just touches the base line: exactly one triangle, with a right angle.",
    "one-long":
      "Side {x} is at least as long as the other known side, so only one triangle fits.",
    "one-obtuse":
      "The known angle is obtuse and its opposite side is the longest, so exactly one triangle fits.",
    two: "The height h is less than side {x}, which is less than the other known side, so the side can swing to two positions: two different triangles fit.",
  },
  diagramTitle:
    "The triangle drawn to scale with its sides and angles labelled; entered values in blue and solved values in orange",
  downloadPng: "Download diagram (PNG)",
} as const;

export type TriangleStrings = typeof TRIANGLE_SOLVER_STRINGS;

type ExampleId = Exclude<keyof TriangleStrings["examples"], "none">;
type Cells = [string, string, string];

const EXAMPLE_ORDER: ExampleId[] = [
  "sss",
  "sas",
  "asa",
  "aas",
  "ssaTwo",
  "ssaNone",
];
const EXAMPLE_VALUES: Record<ExampleId, [Cells, Cells]> = {
  sss: [
    ["7", "8", "9"],
    ["", "", ""],
  ],
  sas: [
    ["", "7", "10"],
    ["50", "", ""],
  ],
  asa: [
    ["", "", "10"],
    ["40", "60", ""],
  ],
  aas: [
    ["8", "", ""],
    ["40", "60", ""],
  ],
  ssaTwo: [
    ["5", "8", ""],
    ["30", "", ""],
  ],
  ssaNone: [
    ["3", "8", ""],
    ["30", "", ""],
  ],
};
const NAMES = ["a", "b", "c"] as const;
const CAPITALS = ["A", "B", "C"] as const;

function readGiven(
  sides: Cells,
  angles: Cells,
  unit: AngleUnit
): Given | "invalid" {
  const parsedSides: (number | null)[] = [];
  for (const text of sides) {
    if (text.trim() === "") {
      parsedSides.push(null);
      continue;
    }
    const value = parseNumberInput(text);
    if (value === null || value <= 0) {
      return "invalid";
    }
    parsedSides.push(value);
  }
  const parsedAngles: (number | null)[] = [];
  for (const text of angles) {
    if (text.trim() === "") {
      parsedAngles.push(null);
      continue;
    }
    const parsed = parseAngle(text, unit);
    if (parsed === null || parsed.degrees <= 0 || parsed.degrees >= 180) {
      return "invalid";
    }
    parsedAngles.push((parsed.degrees * Math.PI) / 180);
  }
  return { sides: parsedSides, angles: parsedAngles };
}

function Outcome({
  sides,
  angles,
  unit,
  ctxBase,
}: {
  sides: Cells;
  angles: Cells;
  unit: AngleUnit;
  ctxBase: Pick<TriangleCtx, "strings" | "fmt" | "unit">;
}) {
  const { strings } = ctxBase;
  const given = readGiven(sides, angles, unit);
  if (given === "invalid") {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  const solution = solveTriangle(given);
  if (solution === "need-three") {
    return <Notice>{strings.needThree}</Notice>;
  }
  if (solution === "too-many") {
    return <ErrorNotice>{strings.tooMany}</ErrorNotice>;
  }
  if (solution === "need-side") {
    return <ErrorNotice>{strings.needSide}</ErrorNotice>;
  }
  if (solution === "angle-sum") {
    return <ErrorNotice>{strings.angleSum}</ErrorNotice>;
  }
  if (solution === "inequality") {
    return <ErrorNotice>{strings.inequality}</ErrorNotice>;
  }
  const ctx: TriangleCtx = {
    ...ctxBase,
    givenSides: given.sides.map((x) => x !== null) as [
      boolean,
      boolean,
      boolean,
    ],
    givenAngles: given.angles.map((x) => x !== null) as [
      boolean,
      boolean,
      boolean,
    ],
  };
  return <TriangleResults ctx={ctx} solution={solution} />;
}

export default function TriangleSolverWidget() {
  const strings = useToolUi(TRIANGLE_SOLVER_STRINGS);
  const fmt = useToolNumber();
  const [unit, setUnit] = useState<AngleUnit>("degrees");
  const [sides, setSides] = useState<Cells>(EXAMPLE_VALUES.ssaTwo[0]);
  const [angles, setAngles] = useState<Cells>(EXAMPLE_VALUES.ssaTwo[1]);
  const [example, setExample] = useState<"none" | ExampleId>("ssaTwo");
  const load = (id: "none" | ExampleId) => {
    setExample(id);
    if (id !== "none") {
      setUnit("degrees");
      setSides(EXAMPLE_VALUES[id][0]);
      setAngles(EXAMPLE_VALUES[id][1]);
    }
  };
  const edit = (set: typeof setSides, index: number) => (value: string) => {
    setExample("none");
    set((prev) => prev.map((x, i) => (i === index ? value : x)) as Cells);
  };
  return (
    <>
      <p className="text-muted-foreground text-sm">{strings.intro}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ts-example"
          label={strings.exampleLabel}
          onChange={load}
          options={[
            { value: "none", label: strings.examples.none },
            ...EXAMPLE_ORDER.map((id) => ({
              value: id,
              label: strings.examples[id],
            })),
          ]}
          value={example}
        />
        <PillGroup
          label={strings.unitLabel}
          name="ts-unit"
          onChange={setUnit}
          options={(["degrees", "radians"] as const).map((id) => ({
            value: id,
            label: strings.units[id],
          }))}
          value={unit}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.sidesLegend}
          </legend>
          <div className="grid grid-cols-3 gap-3">
            {NAMES.map((name, i) => (
              <TextField
                id={`ts-side-${name}`}
                key={name}
                label={t(strings.sideInput, { name })}
                mono
                numeric
                onChange={edit(setSides, i)}
                value={sides[i]}
              />
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.anglesLegend}
          </legend>
          <div className="grid grid-cols-3 gap-3">
            {CAPITALS.map((name, i) => (
              <TextField
                id={`ts-angle-${name}`}
                key={name}
                label={t(strings.angleInput, { name })}
                mono
                numeric={unit === "degrees"}
                onChange={edit(setAngles, i)}
                value={angles[i]}
              />
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            {unit === "degrees"
              ? strings.angleHintDegrees
              : strings.angleHintRadians}
          </p>
        </fieldset>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          angles={angles}
          ctxBase={{ strings, fmt, unit }}
          sides={sides}
          unit={unit}
        />
      </section>
    </>
  );
}
